import { Box, Collapse, IconButton, Tooltip, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { formatDisplayDateTime } from "utils/dateFormat";
import { useState } from "react";
import { formatTermSwapWarnings } from "./termSwapNoteUtils";
import {
  formatSuperAdminTermEditWarnings,
  parseSuperAdminTermEditNote,
} from "./superAdminTermEditNoteUtils";

const formatAuditLogDetails = (action, details) => {
  if (!details) return details;

  if (action === "TERM_SWAP") {
    try {
      const data = typeof details === "string" ? JSON.parse(details) : details;
      const warnings = formatTermSwapWarnings(data.warnings);
      const lines = [
        `Swapped Term T${data.termA} ↔ Term T${data.termB}`,
        "Term labels exchanged; schedule, payments, notes, commission, bonus, and claims stayed on their physical rows.",
      ];
      if (warnings.length > 0) {
        lines.push(`Warnings: ${warnings.join(" ")}`);
      }
      return lines.join(" ");
    } catch {
      return details;
    }
  }

  if (action === "SUPERADMIN_TERM_EDIT" || action === "SUPERADMIN_SCHEDULE_EDIT") {
    try {
      const data = typeof details === "string" ? JSON.parse(details) : details;
      const parsed = parseSuperAdminTermEditNote(
        `[SUPERADMIN_TERM_EDIT]${JSON.stringify(data)}[/SUPERADMIN_TERM_EDIT]`
      );
      if (!parsed) return details;
      const changeLines = (parsed.changes || []).map(
        (c) => `${c.label || c.field}: ${c.from} → ${c.to}`
      );
      const warnings = formatSuperAdminTermEditWarnings(parsed.warnings);
      const lines = [
        `Term T${parsed.termNumber || "?"} edited from ${
          parsed.source === "DIRECT_ACCOUNT" ? "Direct Account" : "Manage Admissions"
        }`,
        changeLines.join("; "),
      ];
      if (warnings.length > 0) {
        lines.push(`Warnings: ${warnings.join(" ")}`);
      }
      return lines.filter(Boolean).join(" ");
    } catch {
      return details;
    }
  }

  return details;
};

const auditActionLabel = (action) => {
  if (action === "TERM_SWAP") return "Term swap";
  if (action === "SUPERADMIN_TERM_EDIT" || action === "SUPERADMIN_SCHEDULE_EDIT") {
    return "Super-admin term edit";
  }
  return action;
};

const AccountVerificationAuditLogExpander = ({ auditLogs = [] }) => {
  const [open, setOpen] = useState(false);
  if (!auditLogs.length) return null;

  return (
    <Box>
      <Tooltip title={open ? "Hide history" : "Show history"}>
        <IconButton size="small" onClick={() => setOpen((v) => !v)}>
          {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Tooltip>
      <Collapse in={open}>
        <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0, pl: 1 }}>
          {auditLogs.map((log, i) => (
            <Box
              component="li"
              key={i}
              sx={{ py: 0.5, borderLeft: "2px solid #e0e0e0", pl: 1, mb: 0.5 }}
            >
              <Typography variant="caption" fontWeight={600}>
                {auditActionLabel(log.action)}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {log.actorName}
                {log.timestamp ? " · " + formatDisplayDateTime(log.timestamp) : ""}
              </Typography>
              {log.details && (
                <Typography variant="caption" color="text.secondary">
                  {formatAuditLogDetails(log.action, log.details)}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

export default AccountVerificationAuditLogExpander;
